import type { BadgeVariant } from '../../../components/ui/index.js';
import type { ReasoningEvaluationCheck } from '../../../generated/api/types.gen.js';

export const percent = (value: number | null | undefined): string =>
  typeof value === 'number' ? `${Math.round(value * 100)} %` : '—';

/** A check's value as its rule reads it: a count for unfound quotes, a signed return for outcomes, else a share. */
export function checkValue(check: Pick<ReasoningEvaluationCheck, 'key' | 'value'>): string {
  if (check.value === null) return '—';
  if (check.key === 'quotes') return String(check.value);
  if (check.key === 'outcome') return `${check.value.toFixed(2)} % mean return`;
  return percent(check.value);
}

export const CHECK_STATUS: Record<ReasoningEvaluationCheck['status'], { label: string; variant: BadgeVariant }> = {
  pass: { label: 'Pass', variant: 'success' },
  fail: { label: 'Below threshold', variant: 'danger' },
  insufficient: { label: 'Too few to judge', variant: 'neutral' },
  reported: { label: 'Reported', variant: 'info' },
};

/** Where the persona was asked: the local evaluation model, or a named conversation policy (a Claude sample). */
export const askedOn = (surface: string | null, policyId: string | null): string =>
  surface === 'evaluation' ? 'Local model' : policyId ? `Policy ${policyId}` : (surface ?? '—');

export const VERDICT_LABEL: Record<string, string> = {
  held: 'Held',
  broke: 'Broke',
  mixed: 'Mixed',
  inconclusive: 'Inconclusive',
  untestable: 'Untestable',
};

/** A principle's test class (spec account-persona 10 §2): its code and what it claims. */
export const PRINCIPLE_CLASS_LABEL: Record<string, string> = {
  co_movement: 'C1 co-movement',
  lead_lag: 'C2 lead–lag',
  signal_response: 'C3 signal → response',
  calendar: 'C4 calendar',
  event_response: 'C5 event response',
  relative_performance: 'C6 relative performance',
  regime_conditional: 'C7 regime-conditional',
  definitional: 'N1 definitional',
  normative: 'N2 advice',
  about_others: 'N3 about others',
  dated_setup: 'N4 dated setup',
  causal: 'N5 causal',
};

/** A check window's verdict; a check by the program before the windows has none. */
export const WINDOW_LABEL: Record<string, string> = { ...VERDICT_LABEL, 'not windowed': 'Older program (no windows)' };

/** A routed principle's hypothesis, as the finance brain holds it. */
export const HYPOTHESIS_LABEL: Record<string, string> = {
  open: 'Open',
  testing: 'Testing',
  supported: 'Supported',
  refuted: 'Refuted',
  inconclusive: 'Inconclusive',
  expired: 'Expired',
};
